import { useEffect, useState } from 'react'
import { Typography } from 'amo-styling-library'
import { SimpleDashboardLayout } from '../components/layout/SimpleDashboardLayout'
import { ImportExportContainerTable } from '../components/containers/ImportExportContainerTable'
import { ImportDetailModal } from '../components/containers/ImportDetailModal'
import { ExportDetailModal } from '../components/containers/ExportDetailModal'
import { Modal } from '../components/ui/Modal/Modal'
import { ModalHeader } from '../components/ui/Modal/ModalHeader'
import { ModalBody } from '../components/ui/Modal/ModalBody'
import {
  getImportContainers,
  getExportContainers,
  getImportContainerDetail,
  getExportContainerDetail,
} from '../services/containerService'
import { API_ENDPOINTS } from '../config/api'
import type {
  ImportExportContainer,
  ImportContainerDetail,
  ExportContainerDetail,
} from '../types/container'

type TabType = 'import' | 'export'

function TripsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('import')
  const [importContainers, setImportContainers] = useState<ImportExportContainer[]>([])
  const [exportContainers, setExportContainers] = useState<ImportExportContainer[]>([])
  const [loading, setLoading] = useState(true)

  // Modal state
  const [importDetail, setImportDetail] = useState<ImportContainerDetail | null>(null)
  const [exportDetail, setExportDetail] = useState<ExportContainerDetail | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

  // Search form state
  const [searchContainerNr, setSearchContainerNr] = useState('')
  const [searchType, setSearchType] = useState<'import' | 'export'>('import')
  const [searchVolLeeg, setSearchVolLeeg] = useState<'vol' | 'leeg'>('vol')

  const fetchData = async () => {
    setLoading(true)
    const [importData, exportData] = await Promise.all([
      getImportContainers(),
      getExportContainers(),
    ])
    setImportContainers(importData)
    setExportContainers(exportData)
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleImportRowClick = async (container: ImportExportContainer) => {
    const detail = await getImportContainerDetail(container.ritNr)
    if (!detail) return
    setImportDetail(detail)
    setExportDetail(null)
    setIsDetailModalOpen(true)
  }

  const handleExportRowClick = async (container: ImportExportContainer) => {
    const detail = await getExportContainerDetail(container.ritNr)
    if (!detail) return
    setExportDetail(detail)
    setImportDetail(null)
    setIsDetailModalOpen(true)
  }

  const closeDetailModal = () => {
    setIsDetailModalOpen(false)
    setImportDetail(null)
    setExportDetail(null)
  }

  const [searchLoading, setSearchLoading] = useState(false)
  const [searchError, setSearchError] = useState('')

  // Check in container: creates STI activity so it appears in depot, then refresh
  const handleSearchSubmit = async () => {
    if (!searchContainerNr.trim()) return

    setSearchLoading(true)
    setSearchError('')

    try {
      const res = await fetch(API_ENDPOINTS.depot.checkIn, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          containerNumber: searchContainerNr.trim().toUpperCase(),
          type: searchType,
          volLeeg: searchVolLeeg,
        }),
      })

      if (!res.ok) {
        const err = await res.json()
        setSearchError(err.error || 'Container niet gevonden')
        setSearchLoading(false)
        return
      }

      setSearchContainerNr('')
      setIsSearchModalOpen(false)

      // Refresh data — container now has STI activity and will appear at top
      await fetchData()
      setActiveTab(searchType)
    } catch {
      setSearchError('Fout bij het toevoegen van de container')
    } finally {
      setSearchLoading(false)
    }
  }

  return (
    <SimpleDashboardLayout>
      <div className="bg-white rounded-lg p-6 h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <Typography variant="h2" className="text-gray-900">
            Import / Export Overzicht
          </Typography>
          <button
            onClick={() => setIsSearchModalOpen(true)}
            className="px-6 py-2.5 rounded-lg font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
            style={{ backgroundColor: '#DA0000' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#B80000'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#DA0000'}
          >
            Zoek Container
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('import')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'import'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Import ({importContainers.length})
          </button>
          <button
            onClick={() => setActiveTab('export')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'export'
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Export ({exportContainers.length})
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <p className="text-gray-600">Laden...</p>
        ) : (
          <>
            {activeTab === 'import' && (
              <ImportExportContainerTable
                data={importContainers}
                onRowClick={handleImportRowClick}
              />
            )}
            {activeTab === 'export' && (
              <ImportExportContainerTable
                data={exportContainers}
                onRowClick={handleExportRowClick}
              />
            )}
          </>
        )}
      </div>

      {/* Import Detail Modal */}
      <ImportDetailModal
        detail={importDetail}
        isOpen={isDetailModalOpen && importDetail !== null}
        onClose={closeDetailModal}
      />

      {/* Export Detail Modal */}
      <ExportDetailModal
        detail={exportDetail}
        isOpen={isDetailModalOpen && exportDetail !== null}
        onClose={closeDetailModal}
      />

      {/* Search Container Modal */}
      <Modal isOpen={isSearchModalOpen} onClose={() => setIsSearchModalOpen(false)}>
        <ModalHeader title="Zoek Container" onClose={() => setIsSearchModalOpen(false)} />
        <ModalBody>
          <div className="space-y-4">
            {/* Container Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Container Nummer
              </label>
              <input
                type="text"
                value={searchContainerNr}
                onChange={(e) => setSearchContainerNr(e.target.value)}
                placeholder="bv. CIPU5043260"
                className="w-full px-3 py-2 bg-white border border-red-300 rounded-lg outline-none focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
              />
            </div>

            {/* Import / Export dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Import / Export
              </label>
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'import' | 'export')}
                className="w-full px-3 py-2 bg-white border border-red-300 rounded-lg outline-none focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
              >
                <option value="import">Import</option>
                <option value="export">Export</option>
              </select>
            </div>

            {/* Vol / Leeg dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vol / Leeg
              </label>
              <select
                value={searchVolLeeg}
                onChange={(e) => setSearchVolLeeg(e.target.value as 'vol' | 'leeg')}
                className="w-full px-3 py-2 bg-white border border-red-300 rounded-lg outline-none focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
              >
                <option value="vol">Vol</option>
                <option value="leeg">Leeg</option>
              </select>
            </div>

            {/* Error */}
            {searchError && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                {searchError}
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSearchSubmit}
              disabled={!searchContainerNr.trim() || searchLoading}
              className="w-full px-6 py-2.5 rounded-lg font-medium text-white transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#DA0000' }}
            >
              {searchLoading ? 'Bezig...' : 'Toevoegen'}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </SimpleDashboardLayout>
  )
}

export default TripsPage
